import { useAtomValue } from "jotai";
import { useAuth } from "@/hooks/useAuth";
import { currentUserAtom } from "@/store/userAtoms";
import { ApprovalStatus, MembershipStatus } from "@models/user";

// The membership statuses the API's active-customer wall lets shop — the wall
// that guards GET /categories and product suggest (#416).
//   Active               — full access (admins are always reported Active).
//   NoLocationMembership — full access too: the wall is ORG-level (a paid-up,
//                          in-good-standing org with an approved location), and
//                          this member is only unseated on its locations. The
//                          gate admits them and the store explains why it is
//                          near-empty (#417), so their shop entry points stay.
//
// An allow-list that fails CLOSED: None, absent, null or a value this build does
// not know means no shop entry points. This deliberately differs from
// isMembershipWalled, which fails OPEN because walling a paying customer's pages
// behind lapsed-payment copy is the worse failure. Here the costs run the other
// way: a hidden Search button is harmless, a shown one is a 403 and a toast.
const SHOPPABLE_MEMBERSHIP_STATUSES: ReadonlyArray<MembershipStatus | undefined> = [
    MembershipStatus.Active,
    MembershipStatus.NoLocationMembership,
];

// The one "can shop" rule: signed in, approved, and a membership the API lets
// shop. Not-yet-approved users are walled at /c/pending, lapsed memberships at
// /c/payment-required, and the shopping reads are walled to active customers by
// the API; this keeps the entry points that sit OUTSIDE those page walls (the
// navs on /c/account and the public pages) in step with them. Both the auth and
// the user halves are needed: useEndSessionOnSignOut clears currentUserAtom in
// an effect, so in the commit where MSAL first says signed out the atom still
// holds the approved user.
export const useCanShop = (): boolean => {
    const { isAuthenticated } = useAuth();
    const appUser = useAtomValue(currentUserAtom);
    return (
        isAuthenticated &&
        appUser?.approvalStatus === ApprovalStatus.Approved &&
        SHOPPABLE_MEMBERSHIP_STATUSES.includes(appUser.membershipStatus)
    );
};
